/**
 * АВТОПРИНЯТИЕ ПРИГЛАШЕНИЙ + сигнал наверх «вас добавили».
 *
 * Постановка (решение Антона, 2026-09-12):
 *  1. Любое приглашение в комнату принимается автоматически (joinRoom),
 *     кнопок «принять» больше нет. Различать «из звонка / не из звонка»
 *     НЕ нужно — признак снят.
 *  2. Человек СРАЗУ видит заметное сообщение «Вас добавили в …» — его
 *     показывает App по колбэку onAutoJoined отсюда (там же дедупликация
 *     в одну строку со счётчиком).
 *  3. ЕДИНСТВЕННОЕ ОГРАНИЧЕНИЕ: автопринимаем ТОЛЬКО приглашения,
 *     пришедшие в ТЕКУЩЕЙ сессии клиента. Старые, уже лежащие в списке
 *     приглашений до запуска, — НЕ принимаем.
 *
 * ТОЧНОЕ УСЛОВИЕ ОТСЕЧКИ СТАРЫХ ПРИГЛАШЕНИЙ (причина замеренная, из
 * постановки): при переподключении/первом запуске Matrix отдаёт накопленные
 * события заново, и Room.myMembership="invite" для старых приглашений
 * эмитится в ходе INITIAL sync — ДО того, как клиент дошёл до
 * SyncState.PREPARED. Поэтому условие такое:
 *
 *     автопринимаем invite, ТОЛЬКО ЕСЛИ на момент события MyMembership
 *     первый sync УЖЕ завершён (SyncState стал PREPARED или SYNCING).
 *
 * Это ровно тот же барьер firstSyncDone, что в notifier.ts (там он защищает
 * от лавины уведомлений за всю историю). Приглашения, пришедшие за время
 * офлайна при РЕКОННЕКТЕ (limited sync), приезжают когда PREPARED уже
 * был — их принимаем, и это правильно: они «новые» для человека.
 * Без этого барьера первый запуск влетел бы разом во все приглашения,
 * какие когда-либо приходили аккаунту (замер из постановки).
 *
 * ИСТОЧНИК СОБЫТИЯ — RoomEvent.MyMembership, а не RoomStateEvent.Members:
 * нам нужно членство ТОЛЬКО нашего пользователя, и SDK даёт его одной
 * подпиской с (room, membership, prevMembership), без ручной фильтрации
 * всех чужих member-событий в комнатах.
 */
import {
  ClientEvent,
  EventType,
  RoomEvent,
  SyncState,
  type MatrixClient,
  type Room,
} from "matrix-js-sdk";

import { tf } from "../i18n";
import { humanError } from "../matrix/roomActions";

/** Один факт автопринятия — App показывает его строкой-уведомлением. */
export interface AutoJoinedInvite {
  roomId: string;
  roomName: string;
  /** Кто пригласил (sender m.room.member с membership=invite); может не быть. */
  inviterLabel: string | null;
}

export interface AutoAcceptContext {
  /** Вызывается после УСПЕШНОГО joinRoom — App показывает «Вас добавили…». */
  onAutoJoined(info: AutoJoinedInvite): void;
  /** Ошибка joinRoom — App показывает её полосой, а не глотает. */
  onError(message: string): void;
}

/**
 * Подписаться на приглашения и автопринимать их. Возвращает отписку.
 * Вызывать ОДИН раз на живого клиента (App, эффект по phase==="ready").
 */
export function startInviteAutoAccept(client: MatrixClient, ctx: AutoAcceptContext): () => void {
  const me = client.getUserId();

  /**
   * Барьер первого sync — см. шапку. Если клиент уже жив (стартовый эффект
   * App дождался startClient), считаем sync завершённым сразу: события
   * initial sync к этому моменту уже эмитнуты и подписку не заденут.
   */
  let firstSyncDone =
    client.getSyncState() === SyncState.Prepared || client.getSyncState() === SyncState.Syncing;
  // Тип параметра — string, как в notifier.ts: handler ClientEvent.Sync в
  // SDK допускает такую подпись, а единообразие с соседним модулем важнее
  // строгости enum (значения те же: "PREPARED"/"SYNCING").
  const onSync = (state: string) => {
    if (state === SyncState.Prepared || state === SyncState.Syncing) firstSyncDone = true;
  };
  client.on(ClientEvent.Sync, onSync);

  /**
   * Защита от ДВОЙНОГО join одной комнаты: пока летит joinRoom, второе
   * событие MyMembership (дубликат, ретрай sync) не должно отправить ещё
   * один. Ключ — roomId. Запись удаляем в finally (успех/ошибка), а не
   * держим навсегда: ПОВТОРНОЕ приглашение в ту же комнату (нас вышли и
   * позвали снова) — новая ситуация текущей сессии, она обязана снова
   * автоприняться и снова показать строку. Проверка prevMembership !==
   * "invite" выше при этом не мешает: у повторного приглашения prev —
   * "leave"/"join", а не "invite".
   */
  const joining = new Set<string>();

  const onMyMembership = (room: Room, membership: string, prevMembership?: string) => {
    if (membership !== "invite") return;
    // ОТСЕЧКА СТАРЫХ (см. шапку): до первого завершённого sync молчим.
    if (!firstSyncDone) return;
    // Только ПЕРЕХОД в invite из не-invite: обновление уже существующего
    // приглашения (prevMembership === "invite") — это НЕ новое приглашение,
    // а повторная доставка состояния (limited sync, TimelineReset). Без
    // этой проверки реконнект переоткрыл бы уведомление о приглашении,
    // которое человек уже видел.
    if (prevMembership === "invite") return;
    if (joining.has(room.roomId)) return;
    joining.add(room.roomId);

    // Кто пригласил: ищем СВОЁ member-событие с membership=invite и берём
    // его sender. До join полный state комнаты может быть урезан (invite-
    // комнаты отдают stripped state), но m.room.member самого приглашения
    // в нём есть — этого достаточно.
    let inviterLabel: string | null = null;
    if (me) {
      const memberEvent = room.currentState.getStateEvents(EventType.RoomMember, me);
      const sender = memberEvent?.getSender() ?? null;
      if (sender && sender !== me) {
        inviterLabel = room.getMember(sender)?.name ?? sender;
      }
    }
    const roomName = room.name || room.roomId;
    const roomId = room.roomId;

    void client
      .joinRoom(roomId)
      .then(() => {
        ctx.onAutoJoined({ roomId, roomName, inviterLabel });
      })
      .catch((e: unknown) => {
        // join упал (права, сеть, приглашение отозвали): показываем причину,
        // а не делаем вид, что ничего не произошло. Комната при этом останется
        // в списке приглашений — человек может принять её вручную.
        ctx.onError(tf("room.inviteAcceptFailed", { room: roomName, error: humanError(e) }));
      })
      .finally(() => {
        joining.delete(roomId);
      });
  };

  client.on(RoomEvent.MyMembership, onMyMembership);
  return () => {
    client.off(RoomEvent.MyMembership, onMyMembership);
    client.off(ClientEvent.Sync, onSync);
  };
}
